import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Typography,
  Box,
  FormControlLabel,
  Divider
} from '@mui/material'
import PropTypes from 'prop-types'
import { SearchInput, StatusChip } from '../atoms'

const CampaignsDialog = ({
  open,
  onClose,
  automation,
  campaigns,
  searchTerm,
  onSearchChange,
  onToggleCampaign,
  onToggleAllCampaigns
}) => {
  if (!automation) return null

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const enabledCount = campaigns.filter(c => c.enabled).length
  const allEnabled = enabledCount === campaigns.length && campaigns.length > 0
  const someEnabled = enabledCount > 0 && enabledCount < campaigns.length

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { backgroundColor: 'var(--color-card-bg)', color: 'var(--color-text-primary)' } }}
    >
      <DialogTitle style={{ color: 'var(--color-text-primary)' }}>
        Manage Campaigns - {automation.name}
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Select which campaigns should trigger this automation when they reach the usage threshold.
          </Typography>
          
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <StatusChip
              label={`${enabledCount} of ${campaigns.length} campaigns enabled`}
              color={enabledCount > 0 ? 'primary' : 'neutral'}
              variant="outlined"
            />
          </Box>
        </Box>

        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={allEnabled}
                  indeterminate={someEnabled}
                  onChange={(e) => onToggleAllCampaigns(e.target.checked)}
                  color="primary"
                />
              }
              label={allEnabled ? "Disable All Campaigns" : "Enable All Campaigns"}
            />
            <Typography variant="caption" color="textSecondary">
              {enabledCount > 0 && `${enabledCount} enabled`}
            </Typography>
          </Box>
          
          <Divider style={{ margin: '16px 0' }} />
          
          <SearchInput
            fullWidth
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </Box>

        {filteredCampaigns.length === 0 ? (
          <Box textAlign="center" p={3}>
            <Typography variant="body2" color="textSecondary">
              {searchTerm ? 'No campaigns match your search.' : 'No campaigns available.'}
            </Typography>
          </Box>
        ) : (
          <List style={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredCampaigns.map((campaign) => (
              <ListItem key={campaign.campaign_name} divider>
                <ListItemText
                  primary={campaign.campaign_name}
                  secondary={
                    campaign.plan_minutes 
                      ? `Plan: ${campaign.plan_minutes} minutes`
                      : 'No plan configured'
                  }
                />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={campaign.enabled || false}
                    onChange={(e) => onToggleCampaign(campaign.campaign_name, e.target.checked)}
                    color="primary"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

CampaignsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  automation: PropTypes.object,
  campaigns: PropTypes.array.isRequired,
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onToggleCampaign: PropTypes.func.isRequired,
  onToggleAllCampaigns: PropTypes.func.isRequired
}

export default CampaignsDialog